import { useState, useEffect } from 'react';
import { TEXT_MUTED, BORDER_DEFAULT, TEXT_HEADING, BRAND_PRIMARY, BRAND_PRIMARY_TINT, BG_SHELL, BG_SHELL_HOVER, FONT_LIST_ITEM, FONT_SMALL } from '../../theme';

// ─── DIMENSIONS ──────────────────────────────────────────────────────────────
const EXPANDED_W = 260;
const COLLAPSED_W = 48;

// ─── NAVIGATION STRUCTURE ────────────────────────────────────────────────────
// Matches GRC_ARCHITECTURE.md §12 grouped by ISO clause.
const NAV_STRUCTURE = [
  {
    type: 'item',
    view: 'dashboard',
    label: { es: 'Panel general', en: 'Dashboard' },
  },
  {
    type: 'item',
    view: 'executive-dashboard',
    label: { es: 'Dashboard ejecutivo', en: 'Executive Dashboard' },
  },
  {
    type: 'group',
    key: 'clause4',
    label: { es: 'Cláusula 4 · Contexto', en: 'Clause 4 · Context' },
    items: [
      { view: 'context', label: { es: 'Contexto', en: 'Context' } },
      { view: 'scope',   label: { es: 'Alcance',  en: 'Scope' } },
    ],
  },
  {
    type: 'group',
    key: 'clause5',
    label: { es: 'Cláusula 5 · Liderazgo', en: 'Clause 5 · Leadership' },
    items: [
      { view: 'policy', label: { es: 'Política', en: 'Policy' } },
    ],
  },
  {
    type: 'group',
    key: 'clause6',
    label: { es: 'Cláusula 6 · Planificación', en: 'Clause 6 · Planning' },
    items: [
      { view: 'risks',      label: { es: 'Riesgos',    en: 'Risks' } },
      { view: 'soa',        label: { es: 'SoA',        en: 'SoA' } },
      { view: 'rtp',        label: { es: 'Plan de tratamiento', en: 'Risk Treatment Plan' } },
      { view: 'objectives', label: { es: 'Objetivos',  en: 'Objectives' } },
    ],
  },
  {
    type: 'group',
    key: 'clause7',
    label: { es: 'Cláusula 7 · Soporte', en: 'Clause 7 · Support' },
    items: [
      { view: 'competence', label: { es: 'Competencia',     en: 'Competence' } },
      { view: 'awareness',  label: { es: 'Concienciación',  en: 'Awareness' } },
      { view: 'documents',  label: { es: 'Documentos',      en: 'Documents' } },
    ],
  },
  {
    type: 'group',
    key: 'clause8',
    label: { es: 'Cláusula 8 · Operación', en: 'Clause 8 · Operation' },
    items: [
      { view: 'assets',    label: { es: 'Activos',     en: 'Assets' } },
      { view: 'suppliers', label: { es: 'Proveedores', en: 'Suppliers' } },
      { view: 'incidents', label: { es: 'Incidentes',  en: 'Incidents' } },
    ],
  },
  {
    type: 'group',
    key: 'clause9',
    label: { es: 'Cláusula 9 · Evaluación', en: 'Clause 9 · Evaluation' },
    items: [
      { view: 'metrics',              label: { es: 'Métricas',    en: 'Metrics' } },
      { view: 'audits',               label: { es: 'Auditorías',  en: 'Audits' } },
      { view: 'management-reviews',   label: { es: 'Revisiones',  en: 'Reviews' } },
    ],
  },
  {
    type: 'group',
    key: 'clause10',
    label: { es: 'Cláusula 10 · Mejora', en: 'Clause 10 · Improvement' },
    items: [
      { view: 'nonconformities', label: { es: 'No conformidades', en: 'Nonconformities' } },
    ],
  },
  {
    type: 'group',
    key: 'registers',
    label: { es: 'Registros', en: 'Registers' },
    items: [
      { view: 'authorities',    label: { es: 'Autoridades',    en: 'Authorities' } },
      { view: 'special-interest-groups', label: { es: 'Grupos de interés', en: 'Special interest groups' } },
      { view: 'customers',      label: { es: 'Clientes',       en: 'Customers' } },
      { view: 'legal-register', label: { es: 'Registro legal', en: 'Legal Register' } },
    ],
  },
];

const BOTTOM_ITEMS = [
  { view: 'settings', label: { es: 'Configuración', en: 'Settings' } },
];

// ─── COMPONENT ────────────────────────────────────────────────────────────────
export default function Sidebar({ activeView, onNavigate, lang }) {
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem('grc-sidebar-collapsed') === 'true'
  );

  const [collapsedGroups, setCollapsedGroups] = useState(
    () => new Set(NAV_STRUCTURE.filter(n => n.type === 'group').map(n => n.key))
  );

  const [hoveredView, setHoveredView] = useState(null);

  // Auto-expand the group that contains the current active view
  useEffect(() => {
    setCollapsedGroups(prev => {
      const group = NAV_STRUCTURE.find(
        n => n.type === 'group' && n.items.some(i => i.view === activeView)
      );
      if (!group || !prev.has(group.key)) return prev;
      const next = new Set(prev);
      next.delete(group.key);
      return next;
    });
  }, [activeView]);

  const width = collapsed ? COLLAPSED_W : EXPANDED_W;
  const t = (obj) => (typeof obj === 'string' ? obj : (obj[lang] || obj.en));

  const toggleCollapsed = () => {
    const next = !collapsed;
    localStorage.setItem('grc-sidebar-collapsed', String(next));
    setCollapsed(next);
  };

  const toggleGroup = (key) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      localStorage.setItem('grc-sidebar-groups', JSON.stringify([...next]));
      return next;
    });
  };

  const renderItem = (item, isSubItem = false) => {
    const isActive = item.view === activeView;
    const isHovered = item.view === hoveredView;
    return (
      <button
        key={item.view}
        onClick={() => onNavigate(item.view)}
        onMouseEnter={() => setHoveredView(item.view)}
        onMouseLeave={() => setHoveredView(null)}
        title={collapsed ? t(item.label) : undefined}
        style={{
          ...FONT_LIST_ITEM,
          fontWeight: isSubItem ? 400 : 700,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          gap: collapsed ? 0 : '0.5rem',
          width: '100%',
          margin: 0,
          padding: collapsed ? '0.6rem 0' : `0.45rem 0.75rem 0.45rem ${isSubItem ? '1.5rem' : '0.75rem'}`,
          background: isActive ? BRAND_PRIMARY_TINT : isHovered ? BG_SHELL_HOVER : 'transparent',
          border: 'none',
          borderLeft: isActive ? `2px solid ${BRAND_PRIMARY}` : '2px solid transparent',
          cursor: 'pointer',
          color: isActive ? BRAND_PRIMARY : TEXT_MUTED,
          textAlign: 'left',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          boxSizing: 'border-box',
          userSelect: 'none',
          flexShrink: 0,
        }}
      >
        {!collapsed && (
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {t(item.label)}
          </span>
        )}
      </button>
    );
  };

  return (
    <div style={{
      width,
      minWidth: width,
      background: BG_SHELL,
      borderRight: `1px solid ${BORDER_DEFAULT}`,
      display: 'flex',
      flexDirection: 'column',
      boxSizing: 'border-box',
      position: 'sticky',
      top: 52,
      height: 'calc(100vh - 52px)',
      overflowX: 'hidden',
      flexShrink: 0,
      transition: 'width 0.2s ease, min-width 0.2s ease',
    }}>
      {/* Collapse/expand toggle */}
      <button
        onClick={toggleCollapsed}
        title={collapsed
          ? (lang === 'es' ? 'Expandir' : 'Expand')
          : (lang === 'es' ? 'Contraer' : 'Collapse')
        }
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-end',
          width: '100%',
          margin: 0,
          padding: '0.5rem 0.75rem',
          background: 'transparent',
          border: 'none',
          borderBottom: `1px solid ${BORDER_DEFAULT}`,
          cursor: 'pointer',
          flexShrink: 0,
          boxSizing: 'border-box',
        }}
      >
        <span style={{ color: TEXT_MUTED, lineHeight: 0 }}>
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="1.75" y="2.75" width="4.25" height="12.5" rx="2" fill="currentColor" fillOpacity="0.25" />
            <line x1="6" y1="2" x2="6" y2="16" stroke="currentColor" strokeWidth="1.5" />
            <rect x="1" y="2" width="16" height="14" rx="3" stroke="currentColor" strokeWidth="1.5" />
          </svg>
        </span>
      </button>

      {/* Scrollable navigation area */}
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        {NAV_STRUCTURE.map(node => {
          if (node.type === 'item') {
            return renderItem(node);
          }

          // Group node — not rendered when collapsed
          if (collapsed) {
            return null;
          }

          const isGroupCollapsed = collapsedGroups.has(node.key);
          return (
            <div key={node.key}>
              <button
                onClick={() => toggleGroup(node.key)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  margin: 0,
                  padding: '0.55rem 0.75rem 0.3rem',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  boxSizing: 'border-box',
                }}
              >
                <span style={{
                  ...FONT_LIST_ITEM,
                  color: TEXT_HEADING,
                  fontWeight: 700,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                  textAlign: 'left',
                }}>
                  {t(node.label)}
                </span>
                <span style={{
                  ...FONT_SMALL,
                  color: TEXT_MUTED,
                  marginLeft: '0.25rem',
                  flexShrink: 0,
                }}>
                  {isGroupCollapsed ? '▸' : '▾'}
                </span>
              </button>
              {!isGroupCollapsed && node.items.map(item => renderItem(item, true))}
            </div>
          );
        })}
      </div>

      {/* Bottom standalone items */}
      <div style={{ borderTop: `1px solid ${BORDER_DEFAULT}`, flexShrink: 0 }}>
        {BOTTOM_ITEMS.map(item => {
          if (item.view === 'settings') {
            const isActive = item.view === activeView;
            const isHovered = item.view === hoveredView;
            return (
              <button
                key={item.view}
                onClick={() => onNavigate(item.view)}
                onMouseEnter={() => setHoveredView(item.view)}
                onMouseLeave={() => setHoveredView(null)}
                title={t(item.label)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  margin: 0,
                  padding: '0.6rem 0',
                  background: isActive ? BRAND_PRIMARY_TINT : isHovered ? BG_SHELL_HOVER : 'transparent',
                  border: 'none',
                  borderLeft: isActive ? `2px solid ${BRAND_PRIMARY}` : '2px solid transparent',
                  cursor: 'pointer',
                  color: isActive ? BRAND_PRIMARY : TEXT_MUTED,
                  fontSize: '1rem',
                  boxSizing: 'border-box',
                  userSelect: 'none',
                  flexShrink: 0,
                }}
              >
                <svg width="18" height="18" viewBox="0 0 18 18" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" d="M7.78 1.3 10.22 1.3 9.86 3.57 12.23 4.55 13.58 2.69 15.31 4.42 13.45 5.77 14.43 8.14 16.7 7.78 16.7 10.22 14.43 9.86 13.45 12.23 15.31 13.58 13.58 15.31 12.23 13.45 9.86 14.43 10.22 16.7 7.78 16.7 8.14 14.43 5.77 13.45 4.42 15.31 2.69 13.58 4.55 12.23 3.57 9.86 1.3 10.22 1.3 7.78 3.57 8.14 4.55 5.77 2.69 4.42 4.42 2.69 5.77 4.55 8.14 3.57Z M9 6.5A2.5 2.5 0 1 0 9 11.5A2.5 2.5 0 1 0 9 6.5Z" />
                </svg>
              </button>
            );
          }
          return renderItem(item);
        })}
      </div>
    </div>
  );
}
